'use client';

import { useEffect, useState } from 'react';
import axios from 'axios';
import { isRole, Role } from '@/utils/auth/isRole';
import { KindeAccessToken } from '@kinde-oss/kinde-auth-nextjs/types';

declare global {
  interface Window {
    Intercom?: (...args: unknown[]) => void;
  }
}

interface IntercomClientProps {
  accessToken: KindeAccessToken | null;
}

export const IntercomClient = ({ accessToken }: IntercomClientProps) => {
  const [hmac, setHmac] = useState('');

  // 1) Fetch the HMAC whenever the user/sub changes
  useEffect(() => {
    const fetchHmac = async () => {
      if (!accessToken?.sub) return;

      try {
        const res = await axios.get(
          `https://intercom-hmac-1087319145919.europe-west4.run.app/hmac?userIdentifier=${accessToken.sub}`,
        );
        setHmac(res.data);
      } catch (error) {
        console.error('Error fetching HMAC:', error);
      }
    };

    fetchHmac();
  }, [accessToken?.sub]);

  // 2) Initialize or update Intercom when we have all the data we need
  useEffect(() => {
    if (!accessToken || !isRole(Role.Admin, accessToken)) {
      return;
    }

    const intercomSettings: Record<string, unknown> = {
      api_base: 'https://api-iam.intercom.io',
      app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
    };

    // Only add user details if we have sub, email, and hmac
    if (accessToken?.sub && accessToken.email && hmac) {
      intercomSettings.user_id = accessToken.sub;
      intercomSettings.email = accessToken.email;
      intercomSettings.user_hash = hmac;
      intercomSettings.user_type = 'admin';
    }

    // Ensure we’re in the browser
    if (typeof window !== 'undefined') {
      (
        window as unknown as { intercomSettings: Record<string, unknown> }
      ).intercomSettings = intercomSettings;

      // If Intercom is already loaded, just update it
      if (window.Intercom) {
        window.Intercom('reattach_activator');
        window.Intercom('update', intercomSettings);
      } else {
        // Otherwise, inject the Intercom script
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://widget.intercom.io/widget/${process.env.NEXT_PUBLIC_INTERCOM_APP_ID}`;
        script.onload = () => {
          if (window.Intercom) {
            window.Intercom('update', intercomSettings);
          }
        };
        document.body.appendChild(script);
      }
    }
  }, [accessToken, hmac]);

  // Moved the early return **AFTER** the hooks
  if (!accessToken || !isRole(Role.Admin, accessToken)) {
    return null;
  }

  // This component does not render anything visually
  return null;
};
